import '@theme/product-form';
import '@theme/theme-drawer';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent } from '@shopify/events';

/* Keep the native picker and form; await one cart write before showing its result. */
class MCPurchaseSheet extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.abort = new AbortController();
    this.section = this.closest('.shopify-section');
    this.dialog = this.querySelector('dialog');
    this.quantity = this.querySelector('[data-sheet-quantity]');
    this.confirmButton = this.querySelector('[data-sheet-confirm]');
    this.error = this.querySelector('[data-sheet-error]');
    this.generation ??= 0;
    this.selectedVariant ??= JSON.parse(this.querySelector('[data-sheet-variant]').textContent || 'null');
    const signal = this.abort.signal;
    // Module scripts run after the section's form and launchers have been parsed.
    if (!this.section || !this.form || !this.nativeAdd || !this.nativeBuy || !this.dialog.showModal) return;
    this.dataset.ready = '';
    this.section.classList.add('mc-sheet-enabled');
    this.showLaunchers();
    this.section.addEventListener('click', event => {
      const launcher = event.target.closest('[data-mc-sheet-open]');
      if (!launcher) return;
      event.preventDefault();
      this.open(launcher.dataset.mcSheetOpen, launcher);
    }, { signal });
    this.querySelector('[data-sheet-close]').addEventListener('click', () => this.dialog.close(), { signal });
    this.dialog.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }, { signal });
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('mc-purchase-sheet-open');
      this.launcher?.focus({ preventScroll: true });
    }, { signal });
    this.dialog.addEventListener('keydown', event => {
      if (event.key === 'Enter' && event.target === this.quantity) {
        event.preventDefault();
        this.confirm();
      }
    }, { signal });
    this.quantity.addEventListener('input', () => this.syncStepper(), { signal });
    this.querySelectorAll('[data-sheet-step]').forEach(button => button.addEventListener('click', () => {
      if (!this.quantity.value) this.quantity.value = this.quantity.min || '1';
      Number(button.dataset.sheetStep) > 0 ? this.quantity.stepUp() : this.quantity.stepDown();
      this.syncStepper();
    }, { signal }));
    this.confirmButton.addEventListener('click', () => this.confirm(), { signal });
    this.section.addEventListener('shopify:product:select', event => this.variantChanged(event), { signal });
    window.addEventListener('pageshow', event => {
      if (event.persisted) this.restorePage();
    }, { signal });
    // The theme can morph the form controls when quantity rules change.
    this.observer = new MutationObserver(() => {
      this.showLaunchers();
    });
    this.observer.observe(this.formComponent, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class'] });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.section?.classList.remove('mc-sheet-enabled');
    document.documentElement.classList.remove('mc-purchase-sheet-open');
  }

  get formComponent() { return this.section?.querySelector('product-form-component[data-cart-redirect-url]'); }
  get form() { return this.formComponent?.querySelector('form'); }
  get nativeAdd() { return this.form?.querySelector('[data-mc-native-actions] button[name="add"]'); }
  get nativeBuy() { return this.form?.querySelector('[data-melachill-direct-checkout]'); }
  get nativeQuantity() { return this.form?.querySelector('input[name="quantity"]'); }
  get cartUrl() { return this.formComponent?.dataset.cartRedirectUrl || '/cart'; }

  restorePage() {
    // Back/forward cache preserves JS state, including an old "Processing" lock.
    // If a write was not acknowledged, send the buyer to the cart to check it;
    // do not clear the lock into a silently repeatable purchase.
    this.uncertain = Boolean(this.submitting || this.uncertain);
    this.submitting = false;
    this.completed = false;
    if (this.pending) {
      this.generation++;
      this.pending = false;
      this.variantFailed = true;
    }
    if (this.uncertain) this.fail('Please check your cart before adding this item again.');
    else {
      this.clearError();
      this.syncButton();
    }
  }

  showLaunchers() {
    this.section.querySelectorAll('.mc-purchase-launchers[hidden]').forEach(node => { node.hidden = false; });
  }

  open(mode, launcher) {
    if (!this.submitting && !this.completed && !this.uncertain) this.mode = mode === 'buy' ? 'buy' : 'add';
    this.dialog.dataset.mode = this.mode;
    this.launcher = launcher;
    if (!this.submitting && !this.completed && !this.uncertain) {
      this.clearError();
      this.syncQuantity();
    }
    this.syncButton();
    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.classList.add('mc-purchase-sheet-open');
  }

  syncQuantity() {
    const source = this.nativeQuantity;
    this.quantity.min = source?.min || '1';
    this.quantity.step = source?.step || '1';
    const max = source?.max;
    const cartQuantity = Number(source?.dataset.cartQuantity) || 0;
    if (max) this.quantity.max = String(Math.max(0, Number(max) - cartQuantity));
    else this.quantity.removeAttribute('max');
    this.quantity.value = source?.value || this.quantity.min;
    this.syncStepper();
  }

  syncStepper() {
    const value = Number(this.quantity.value);
    const locked = Boolean(this.submitting || this.completed || this.uncertain);
    this.querySelector('[data-sheet-step="-1"]').disabled = locked || value <= Number(this.quantity.min);
    this.querySelector('[data-sheet-step="1"]').disabled = locked || (Boolean(this.quantity.max) && value >= Number(this.quantity.max));
    if (this.nativeQuantity && this.quantity.checkValidity()) this.nativeQuantity.value = this.quantity.value;
  }

  syncButton() {
    const unavailable = !this.selectedVariant?.available || !this.nativeAdd || this.nativeAdd.disabled;
    const viewCart = this.uncertain || this.completed;
    this.confirmButton.disabled = !viewCart && Boolean(this.pending || this.variantFailed || this.submitting || unavailable);
    this.confirmButton.textContent = viewCart ? 'View cart' : this.submitting ? 'Processing…' : this.pending ? 'Updating…' : this.variantFailed ? 'Please select an option again' : unavailable ? 'Unavailable' : this.mode === 'buy' ? 'Buy now' : 'Add to cart';
    this.dialog.setAttribute('aria-busy', String(Boolean(this.pending || this.submitting)));
    const locked = Boolean(this.submitting || viewCart);
    this.quantity.disabled = locked;
    this.querySelectorAll('variant-picker fieldset, variant-picker select').forEach(node => { node.disabled = locked; });
    this.syncStepper();
  }

  async variantChanged(event) {
    if (!this.contains(event.target)) return;
    const generation = ++this.generation;
    this.pending = true;
    this.variantFailed = false;
    this.clearError();
    this.syncButton();
    try {
      const { detail } = await event.promise;
      if (generation !== this.generation || !this.isConnected) return;
      if (!detail?.html) throw new Error('Missing variant response');
      const fresh = detail.html.querySelector('mc-purchase-sheet');
      if (fresh) {
        for (const selector of ['[data-sheet-price]', '[data-sheet-selection]']) {
          const value = fresh.querySelector(selector);
          if (value) this.querySelector(selector).textContent = value.textContent;
        }
        const image = fresh.querySelector('.mc-purchase-sheet__image');
        const current = this.querySelector('.mc-purchase-sheet__image');
        if (image && current) {
          for (const attribute of ['src', 'srcset', 'alt']) {
            const value = image.getAttribute(attribute);
            if (value !== null) current.setAttribute(attribute, value);
          }
        }
      }
      // Other section listeners update the native form within the same microtask turn.
      await Promise.resolve();
      if (generation !== this.generation) return;
      // Some event-library versions omit the legacy resource payload. Use the
      // native picker's server-rendered selected-variant JSON, never a guessed ID.
      const selectedJSON = fresh?.querySelector('[data-sheet-variant]')?.textContent;
      const selected = detail.resource ?? JSON.parse(selectedJSON || 'null');
      this.selectedVariant = selected;
      const variantInput = this.form?.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = selected?.id ? String(selected.id) : '';
      if (this.nativeAdd) this.nativeAdd.disabled = !selected?.available;
      if (this.nativeBuy) this.nativeBuy.disabled = !selected?.available;
      this.syncQuantity();
    } catch (error) {
      if (generation !== this.generation) return;
      this.variantFailed = true;
      this.fail('Unable to update this option. Please select it again.');
    } finally {
      if (generation === this.generation) {
        this.pending = false;
        this.syncButton();
      }
    }
  }

  async confirm() {
    if (this.uncertain || this.completed) {
      window.location.assign(this.cartUrl);
      return;
    }
    if (this.pending || this.variantFailed || this.submitting || !this.selectedVariant?.available || this.nativeAdd?.disabled) return;
    if (!this.quantity.reportValidity()) return;
    // Finalize the exact server-resolved selection immediately before handing
    // the existing form to its native handler (also protects against late morphs).
    const variantInput = this.form.querySelector('input[name="id"]');
    if (!variantInput || !this.selectedVariant.id) return this.fail('Please select an available option.');
    variantInput.value = String(this.selectedVariant.id);
    if (this.nativeQuantity) this.nativeQuantity.value = this.quantity.value;
    if (!this.form.checkValidity()) {
      // Preserve any existing required personalization or recipient fields.
      this.dialog.close();
      this.form.reportValidity();
      return;
    }
    this.clearError();
    this.submitting = true;
    this.syncButton();
    try {
      // Await the native form's explicit result, not a synthetic click or a
      // drawer refresh event that may never arrive. Duplicate clicks are locked.
      const showCart = this.mode === 'add' && Boolean(document.getElementById('cart-drawer'));
      const result = await this.formComponent.submitPurchase({ showCart });
      this.submitting = false;
      this.completed = true;
      this.syncButton();
      if (this.mode === 'buy') window.location.assign('/checkout');
      else if (!showCart || !await this.showCart(result)) window.location.assign(this.cartUrl);
    } catch (error) {
      this.uncertain = Boolean(error.purchaseStatusUnknown);
      this.fail(this.uncertain
        ? 'We could not confirm the result. Please check your cart before trying again.'
        : error.message || 'Unable to add this item. Please try again.');
    }
  }

  async ensureCartComponents() {
    const ready = () => customElements.get('cart-items-component')
      && customElements.get('cart-quantity-selector-component');
    if (ready()) return;

    // Empty carts omit these modules. Script tags in hydrated section HTML
    // do not execute, so load the interactive controls before showing it.
    // Keep this off the initial page-load path and bound the fallback wait.
    let timeout;
    try {
      await Promise.race([
        Promise.all([
          import('./component-cart-items.js'),
          import('./component-cart-quantity-selector.js'),
        ]),
        new Promise((_, reject) => {
          timeout = setTimeout(() => reject(new Error('Cart controls timed out')), 8000);
        }),
      ]);
      if (!ready()) throw new Error('Cart controls are unavailable');
    } finally {
      clearTimeout(timeout);
    }
  }

  async showCart(result) {
    try {
      // Shopify can acknowledge the add but return a null section. In that
      // case use the full cart; never retry an already successful cart write.
      const html = result.sections?.['cart-drawer-section'];
      if (!html) return false;
      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const state = JSON.parse(fresh.querySelector('[data-mc-cart-state]')?.textContent || 'null');
      if (!state || !Array.isArray(state.items)) return false;
      const cart = CartLinesUpdateEvent.createCartFromAjaxResponse(state);
      await morphSection('cart-drawer-section', html, { mode: 'hydration', injectStylesheet: true });
      // Upgrade cold-cart elements only after their item rows are present.
      await this.ensureCartComponents();
      const drawer = document.getElementById('cart-drawer');
      const items = drawer?.querySelector('cart-items-component');
      if (typeof drawer?.open !== 'function' || !items) return false;
      // Let the purchase dialog finish restoring focus before the drawer
      // captures it. The product page itself never navigates or loses scroll.
      if (this.dialog.open) {
        await new Promise(resolve => {
          this.dialog.addEventListener('close', resolve, { once: true });
          this.dialog.close();
        });
      }
      drawer.open();
      // Dispatch from the already-rendered cart so its native listener does
      // not render twice. Counts, quantity limits and announcements stay native.
      items.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        lines: [{ merchandiseId: String(this.selectedVariant.id), quantity: Number(this.quantity.value) }],
        promise: Promise.resolve({
          cart,
          detail: { items: state.items, itemCount: state.item_count, sections: result.sections,
            source: 'mc-purchase-sheet', didError: false },
        }),
      }));
      this.completed = false;
      this.syncButton();
      return true;
    } catch (error) {
      console.warn('[purchase-sheet] Cart display unavailable; opening full cart.', error);
      return false;
    }
  }

  clearError() { this.error.hidden = true; this.error.textContent = ''; }
  fail(message) {
    this.submitting = false;
    this.error.textContent = message;
    this.error.hidden = false;
    this.syncButton();
  }
}

if (!customElements.get('mc-purchase-sheet')) customElements.define('mc-purchase-sheet', MCPurchaseSheet);
